import { openDB } from 'idb';
import { DATA_VERSION } from './version';
import { userCacheScope } from './userScope';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
    this.dbName = null;
  }

  // O nome do banco carrega a CONTA e o USUARIO (#1293). O usuario entrou aqui
  // porque o catalogo de etiquetas deixou de ser igual para todo mundo na conta:
  // etiqueta privada aparece para uns e some para outros. Como o IndexedDB e por
  // navegador, sem o usuario no nome quem entrasse depois leria a lista cacheada
  // de quem entrou antes — e a chave de invalidacao, que e por conta, nao
  // acusaria nada, porque nada mudou na conta.
  //
  // O nome e resolvido no `initDb` e nao no construtor: o `DataManager` nasce no
  // import de `api/labels.js`, quando ainda pode nao haver sessao.
  get dbNameForCurrentUser() {
    return `cw-store-${this.accountId}-${userCacheScope()}`;
  }

  async initDb() {
    const dbName = this.dbNameForCurrentUser;
    // Trocar de usuario na mesma aba fecha o banco anterior em vez de reusar a
    // conexao: sem isso o `this.db` aberto continuaria apontando para o banco de
    // quem saiu.
    if (this.db && this.dbName === dbName) return this.db;
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.dbName = dbName;
    this.db = await openDB(dbName, DATA_VERSION, {
      upgrade(db) {
        db.createObjectStore('cache-keys');
        db.createObjectStore('inbox', { keyPath: 'id' });
        db.createObjectStore('label', { keyPath: 'id' });
        db.createObjectStore('team', { keyPath: 'id' });
      },
    });

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
